import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Calendar, Clock } from "lucide-react";
import biryaniPlate from "@/assets/biryani-branded-watermark.jpg";
import chicken65 from "@/assets/menu/chicken-65-clean.jpg";
import chickenBiryani from "@/assets/menu/chicken-biryani-clean.jpg";
import chickenBreastBiryani from "@/assets/menu/chicken-breast-biryani-branded.jpg";
import dosaVarieties from "@/assets/menu/dosa-varieties-clean.jpg";
import gobiManchurian from "@/assets/menu/gobi-manchurian-clean.jpg";
import paneerBiryani from "@/assets/menu/paneer-biryani-clean.jpg";

interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  available: boolean;
  available_for_preorder?: boolean;
}

// Map menu item names to local images
const getMenuItemImage = (name: string, imageUrl: string | null): string => {
  if (imageUrl) return imageUrl;
  
  const nameLower = name.toLowerCase();
  
  if (nameLower.includes('chicken 65')) return chicken65;
  if (nameLower.includes('chicken breast biryani')) return chickenBreastBiryani;
  if (nameLower.includes('paneer biryani')) return paneerBiryani;
  if (nameLower.includes('gobi manchurian')) return gobiManchurian;
  if (nameLower.includes('dosa') || nameLower.includes('uthappam')) return dosaVarieties;
  if (nameLower.includes('biryani')) return chickenBiryani;
  
  return biryaniPlate; // Default fallback
};

interface ProductCardProps {
  menuItem: MenuItem;
  mode: "order-now" | "pre-order";
  onOrderClick: () => void;
  onPreOrderClick: () => void;
}

const ProductCard = ({ menuItem, mode, onOrderClick, onPreOrderClick }: ProductCardProps) => {
  const itemImage = getMenuItemImage(menuItem.name, menuItem.image_url);
  
  return (
    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="aspect-square overflow-hidden relative">
        <img 
          src={itemImage} 
          alt={menuItem.name} 
          width={670}
          height={670}
          loading="lazy"
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
        />
      </div>
      <CardContent className="p-6">
        <h3 className="text-2xl font-bold text-foreground mb-2">
          {menuItem.name}
        </h3>
        <p className="text-muted-foreground mb-4">
          {menuItem.description || "Authentic aromatic chicken biryani with fragrant basmati rice, tender chicken pieces, and traditional spices"}
        </p>
        <div className="mb-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-semibold text-foreground mb-2">Sides Served:</h4>
          <p className="text-sm text-muted-foreground">
            Gravy, Yogurt Raita, and Gun Powder to manage spicy levels
          </p>
        </div>
        
        {mode === "pre-order" && (
          <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-semibold">Schedule Your Order</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Pre-order available up to 7 days in advance
            </p>
          </div>
        )}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-3xl font-bold text-primary">₱{menuItem.price}</span>
          </div>
          <div className="flex gap-2">
            {mode === "order-now" && (
              <Button size="lg" className="gap-2 flex-1" onClick={onOrderClick}>
                <img src={biryaniPlate} alt="" className="h-5 w-5 rounded-full object-cover" />
                Order Now
              </Button>
            )}
            {mode === "pre-order" && (
              <Button size="lg" className="gap-2 flex-1" onClick={onPreOrderClick}>
                <Calendar className="h-5 w-5" />
                Pre-Order
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
